package com.schoolconnect.app.repository.parentnotices

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.ParentNoticeListItemResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PageRequest
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.util.scLog

data class ParentNoticesPageResult(
    val items: List<ParentNoticeListItemResponse>,
    val totalCount: Int
)

data class ParentNoticeAttachmentData(
    val fileId: String,
    val url: String,
    val name: String,
    val mimeType: String,
    val sizeLabel: String,
    val previewBytes: ByteArray? = null
)

object ParentNoticesRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ParentNoticesRepository"

    suspend fun loadPage(
        studentId: String,
        search: String?,
        status: String?,
        publishedFrom: String?,
        publishedTo: String?,
        pageIndex: Int,
        pageSize: Int,
        sortBy: String,
        order: String
    ): Result<ParentNoticesPageResult> {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) {
            return Result.failure(IllegalArgumentException("A linked student is required to load notices."))
        }
        val normalizedSearch = search?.trim()?.takeIf { it.isNotBlank() }
        val normalizedStatus = status?.trim()?.uppercase()?.takeIf { it.isNotBlank() }
        val safePageSize = pageSize.coerceIn(PaginationPolicy.MIN_PAGE_SIZE, 100)
        val page = PageRequest(pageIndex, safePageSize)
        val safeSort = sortBy.trim().ifBlank { "created_at" }
        val safeOrder = order.trim().lowercase().takeIf { it == "asc" || it == "desc" } ?: "desc"

        scLog("$TAG loadPage start studentId=$normalizedStudentId query=${normalizedSearch ?: "<blank>"} status=${normalizedStatus ?: "<any>"} pageIndex=${page.safePageIndex} pageSize=${page.safePageSize} sortBy=$safeSort order=$safeOrder")
        return KtorApiClient.getParentNotices(
            studentId = normalizedStudentId,
            search = normalizedSearch,
            status = normalizedStatus,
            publishedFrom = publishedFrom?.trim()?.takeIf { it.isNotBlank() },
            publishedTo = publishedTo?.trim()?.takeIf { it.isNotBlank() },
            limit = page.safePageSize,
            offset = page.offset,
            sortBy = safeSort,
            order = safeOrder
        ).mapCatching { items ->
            val totalCount = items.serverTotalCountOrNull()
                ?: throw IllegalStateException("Parent notices response did not include the filtered count.")
            ParentNoticesPageResult(items, totalCount)
        }.onSuccess { result ->
            scLog("$TAG loadPage success studentId=$normalizedStudentId pageIndex=${page.safePageIndex} count=${result.items.size} total=${result.totalCount}")
        }.onFailure { error ->
            scLog("$TAG loadPage failure studentId=$normalizedStudentId pageIndex=${page.safePageIndex}", error)
        }
    }

    suspend fun loadSummary(studentId: String) = KtorApiClient.getParentNoticesSummary(studentId.trim())

    suspend fun loadDetail(noticeId: String) = KtorApiClient.getParentNoticeById(noticeId.trim())

    suspend fun loadAttachmentDetails(fileIds: List<String>, fileUrls: List<String>): List<ParentNoticeAttachmentData> {
        val count = maxOf(fileIds.size, fileUrls.size)
        return (0 until count).map { index ->
            val fileId = fileIds.getOrNull(index).orEmpty()
            val suppliedUrl = fileUrls.getOrNull(index).orEmpty()
            val metadata = fileId.takeIf { it.isNotBlank() }
                ?.let { FileUploadRepository.getMetadata(it).getOrNull() }
            val fileNameFromUrl = suppliedUrl.substringBefore('?').substringAfterLast('/')
            val mimeType = metadata?.detectedMimeType
                ?: metadata?.declaredMimeType
                ?: mimeTypeFromFilename(metadata?.originalFilename.orEmpty().ifBlank { fileNameFromUrl })
            val url = suppliedUrl.ifBlank {
                fileId.takeIf { it.isNotBlank() }
                    ?.let { FileUploadRepository.getDownloadUrl(it).getOrNull()?.url }
                    .orEmpty()
            }
            val previewBytes = if (mimeType.startsWith("image/", ignoreCase = true) && url.isNotBlank()) {
                FileUploadRepository.downloadBytes(url).getOrNull()
            } else null
            ParentNoticeAttachmentData(
                fileId = fileId,
                url = url,
                name = metadata?.originalFilename.orEmpty().ifBlank { "Attachment ${index + 1}" },
                mimeType = mimeType,
                sizeLabel = metadata?.sizeBytes?.let(::formatAttachmentSize) ?: "File",
                previewBytes = previewBytes
            )
        }
    }

    private fun mimeTypeFromFilename(filename: String): String = when (filename.substringAfterLast('.', "").lowercase()) {
        "jpg", "jpeg" -> "image/jpeg"
        "png" -> "image/png"
        "gif" -> "image/gif"
        "webp" -> "image/webp"
        "bmp" -> "image/bmp"
        "svg" -> "image/svg+xml"
        else -> "application/octet-stream"
    }

    private fun formatAttachmentSize(bytes: Long): String = when {
        bytes >= 1024L * 1024L -> "${bytes / (1024L * 1024L)} MB"
        bytes >= 1024L -> "${bytes / 1024L} KB"
        else -> "$bytes B"
    }
}
